import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import PasswordInput from '../../components/PasswordInput';
import './AdminTenantProvisioning.css';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};

/**
 * Admin Tenant Provisioning Page
 * Allows system administrators to create new tenants with full integration
 * 
 * Features:
 * - Interactive tenant creation form
 * - Real-time validation
 * - Stytch & Stripe integration
 * - Subscription tier selection
 * - Progress tracking
 */
const AdminTenantProvisioning = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Clinic information
    clinic_name: '',
    schema_name: '',
    domain: '',
    contact_email: '',
    contact_phone: '',
    timezone: 'Europe/London',
    currency: 'GBP',
    api_key: '',
    
    // Admin user information
    admin_email: '',
    admin_name: '',
    admin_password: '',
    admin_role: 'admin',
    
    // Subscription information
    subscription_tier: 'pro'
  });
  
  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, isAcceptable: false });
  
  // Subscription tiers configuration
  const subscriptionTiers = {
    free: {
      display_name: "Free Plan",
      monthly_price: "£0.00",
      features: ["100 clients", "Basic appointments", "4-day calendar view"],
      stripe_required: false
    },
    basic: {
      display_name: "Basic Plan", 
      monthly_price: "£99.99",
      features: ["1,000 clients", "Email marketing", "5 automations", "4-day calendar view"],
      stripe_required: true
    },
    pro: {
      display_name: "Pro Plan",
      monthly_price: "£200.00", 
      features: ["10,000 clients", "Advanced automation", "Analytics", "Unlimited calendar"],
      stripe_required: true
    },
    enterprise: {
      display_name: "Enterprise Plan",
      monthly_price: "£350.00",
      features: ["Unlimited clients", "API access", "Custom integrations", "Priority support"],
      stripe_required: true
    },
    internal: {
      display_name: "Internal Plan",
      monthly_price: "£0.00",
      features: ["All features", "System admin", "Testing environment"],
      stripe_required: false
    }
  };

  // Generate schema name from clinic name
  useEffect(() => {
    if (formData.clinic_name) {
      const cleanName = formData.clinic_name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      const randomSuffix = Math.random().toString(36).substring(2, 10);
      setFormData(prev => ({
        ...prev,
        schema_name: `clinic_${cleanName}_${randomSuffix}`
      }));
    }
  }, [formData.clinic_name]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateStep = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        if (!formData.clinic_name.trim()) {
          setError('Clinic name is required');
          return false;
        }
        if (!formData.contact_email.trim()) {
          setError('Contact email is required');
          return false;
        }
        break;
      case 2:
        if (!formData.admin_email.trim()) {
          setError('Admin email is required');
          return false;
        }
        if (!formData.admin_name.trim()) {
          setError('Admin name is required');
          return false;
        }
        if (!formData.admin_password.trim()) {
          setError('Admin password is required');
          return false;
        }
        if (!passwordStrength.isAcceptable) {
          setError('Password does not meet strength requirements');
          return false;
        }
        break;
      case 3:
        // Subscription validation already handled in UI
        break;
      default:
        return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Call the backend tenant provisioning API
      const response = await fetch(`${getApiBaseUrl()}/admin/provision-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          clinic: {
            name: formData.clinic_name,
            schema_name: formData.schema_name,
            domain: formData.domain || null,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone || null,
            timezone: formData.timezone,
            currency: formData.currency,
            api_key: formData.api_key || ''
          },
          admin_user: {
            email: formData.admin_email,
            name: formData.admin_name,
            password: formData.admin_password,
            role: formData.admin_role
          },
          subscription_tier: formData.subscription_tier
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(result);
        setStep(4); // Success step
      } else {
        setError(result.error || 'Failed to create tenant');
      }
    } catch (err) {
      console.error('Tenant provisioning error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      clinic_name: '',
      schema_name: '',
      domain: '',
      contact_email: '',
      contact_phone: '',
      timezone: 'Europe/London',
      currency: 'GBP',
      api_key: '',
      admin_email: '',
      admin_name: '',
      admin_password: '',
      admin_role: 'admin',
      subscription_tier: 'pro'
    });
    setError(null);
    setSuccess(null);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="provisioning-step">
            <h3>Clinic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Clinic Name *</label>
                <input
                  type="text"
                  value={formData.clinic_name}
                  onChange={(e) => handleInputChange('clinic_name', e.target.value)}
                  placeholder="Enter clinic name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Schema Name</label>
                <input
                  type="text"
                  value={formData.schema_name}
                  onChange={(e) => handleInputChange('schema_name', e.target.value)}
                  placeholder="Auto-generated from clinic name"
                  className="form-input"
                />
                <small>Database schema name (auto-generated)</small>
              </div>
              
              <div className="form-group">
                <label>Domain</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  placeholder="example.com (optional)"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Contact Email *</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  placeholder="contact@clinic.com"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  placeholder="+44 20 1234 5678"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className="form-select"
                >
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="form-select"
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>API Key (CRM Integration)</label>
                <input
                  type="text"
                  value={formData.api_key}
                  onChange={(e) => handleInputChange('api_key', e.target.value)}
                  placeholder="Optional API key for CRM integration"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="provisioning-step">
            <h3>Administrator Account</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Admin Email *</label>
                <input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => handleInputChange('admin_email', e.target.value)}
                  placeholder="admin@clinic.com"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Admin Full Name *</label>
                <input
                  type="text"
                  value={formData.admin_name}
                  onChange={(e) => handleInputChange('admin_name', e.target.value)}
                  placeholder="Dr. John Smith"
                  className="form-input"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Admin Password *</label>
                <PasswordInput
                  value={formData.admin_password}
                  onChange={(value) => handleInputChange('admin_password', value)}
                  onStrengthChange={setPasswordStrength}
                  placeholder="Enter secure password"
                  showStrengthIndicator={true}
                />
              </div>
              
              <div className="form-group">
                <label>Admin Role</label>
                <select
                  value={formData.admin_role}
                  onChange={(e) => handleInputChange('admin_role', e.target.value)}
                  className="form-select"
                >
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="practitioner">Practitioner</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="provisioning-step">
            <h3>Subscription Plan</h3>
            <div className="subscription-grid">
              {Object.entries(subscriptionTiers).map(([tier, config]) => (
                <div
                  key={tier}
                  className={`subscription-card ${formData.subscription_tier === tier ? 'selected' : ''}`}
                  onClick={() => handleInputChange('subscription_tier', tier)}
                >
                  <div className="subscription-header">
                    <h4>{config.display_name}</h4>
                    <div className="subscription-price">{config.monthly_price}/month</div>
                  </div>
                  <div className="subscription-features">
                    {config.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        ✅ {feature}
                      </div>
                    ))}
                  </div>
                  {config.stripe_required && (
                    <div className="stripe-notice">
                      💳 Requires Stripe setup
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="confirmation-summary">
              <h4>Provisioning Summary</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Clinic:</strong> {formData.clinic_name}
                </div>
                <div className="summary-item">
                  <strong>Schema:</strong> {formData.schema_name}
                </div>
                <div className="summary-item">
                  <strong>Admin:</strong> {formData.admin_name} ({formData.admin_email})
                </div>
                <div className="summary-item">
                  <strong>Plan:</strong> {subscriptionTiers[formData.subscription_tier].display_name}
                </div>
                <div className="summary-item">
                  <strong>Stytch:</strong> ✅ Organization & user will be created
                </div>
                <div className="summary-item">
                  <strong>Stripe:</strong> {subscriptionTiers[formData.subscription_tier].stripe_required ? '✅ Customer & subscription will be created' : '⏭️ Not required'}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="provisioning-step success-step">
            <div className="success-icon">🎉</div>
            <h3>Tenant Created Successfully!</h3>
            
            {success && (
              <div className="success-details">
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Clinic ID:</strong> {success.clinic_id}
                  </div>
                  <div className="detail-item">
                    <strong>Schema Name:</strong> {success.schema_name}
                  </div>
                  <div className="detail-item">
                    <strong>Stytch Org ID:</strong> {success.stytch_organization_id}
                  </div>
                  {success.stripe_customer_id && (
                    <>
                      <div className="detail-item">
                        <strong>Stripe Customer:</strong> {success.stripe_customer_id}
                      </div>
                      <div className="detail-item">
                        <strong>Stripe Subscription:</strong> {success.stripe_subscription_id}
                      </div>
                    </>
                  )}
                  <div className="detail-item">
                    <strong>Subscription Tier:</strong> {success.subscription_tier}
                  </div>
                </div>
                
                <div className="success-actions">
                  <button onClick={resetForm} className="btn-primary">
                    Create Another Tenant
                  </button>
                  <button onClick={() => window.location.href = '/admin/tenants'} className="btn-secondary">
                    View All Tenants
                  </button>
                </div>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="admin-tenant-provisioning">
        <div className="provisioning-header">
          <h1>Create New Tenant</h1>
          <p>Provision a complete tenant with Stytch authentication and Stripe billing</p>
        </div>
        
        {/* Progress Indicator */}
        <div className="progress-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Clinic Info</div>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Admin User</div>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Subscription</div>
          </div>
          <div className={`step ${step >= 4 ? 'active' : ''}`}>
            <div className="step-number">4</div>
            <div className="step-label">Complete</div>
          </div>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="error-alert">
            <div className="error-icon">⚠️</div>
            <div className="error-message">{error}</div>
          </div>
        )}
        
        {/* Step Content */}
        <div className="provisioning-content">
          {renderStep()}
        </div>
        
        {/* Navigation */}
        {step < 4 && (
          <div className="provisioning-actions">
            {step > 1 && (
              <button onClick={handleBack} className="btn-secondary" disabled={loading}>
                Back
              </button>
            )}
            
            {step < 3 && (
              <button onClick={handleNext} className="btn-primary" disabled={loading}>
                Next
              </button>
            )}
            
            {step === 3 && (
              <button onClick={handleSubmit} className="btn-primary create-tenant" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner small"></div>
                    Creating Tenant...
                  </>
                ) : (
                  'Create Tenant'
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTenantProvisioning; 